import { REGISTRY_BLOCK, type RegistryBlockPresentation, type RegistryBlockType } from '../catalog/platformCatalogIds';
import { THEME_ID, type ThemeId } from '../catalog/platformCatalogIds';

const T = REGISTRY_BLOCK.type;
const P = REGISTRY_BLOCK.presentation;

export type ThemeBlockPresentations = Partial<
  Record<RegistryBlockType, RegistryBlockPresentation>
>;

/**
 * Home (or anywhere) curated menu list — favorites, specials, lunch, happy hour,
 * today’s list, etc. Not the full Speisekarte. Arrangement may differ from the
 * Menu-page full block; PDF viewer is excluded (full document belongs on `/menu`).
 */
export const THEME_HIGHLIGHT_MENU_PRESENTATIONS: Partial<
  Record<ThemeId, RegistryBlockPresentation>
> = {
  [THEME_ID.michelinDark]: P.staticMenuHighlightChefsCut,
  /** Conservatory promenade — not Chef’s Cut, not a zine stack. */
  [THEME_ID.limestoneOrangery]: P.staticMenuHighlightStrip,
  /** Estate plate as the house seal — not Chef’s Cut. */
  [THEME_ID.moorlandHearth]: P.staticMenuHighlightHeroPlate,
  [THEME_ID.bistroBrasserie]: P.staticMenuHighlightZine,
  /** Counter film — numbered Thekenstücke (not Hero Plate jewel). */
  [THEME_ID.hinokiCounter]: P.staticMenuHighlightChefsCut,
  [THEME_ID.lotusSteam]: P.staticMenuHighlightDishCards,
  /** Shopfront price/story tiles — denser than zine polaroid stack. */
  [THEME_ID.tileAwning]: P.staticMenuHighlightPriceTiles,
  /** Lunch / evening court split — not Tile price tiles, not Bistro zine. */
  [THEME_ID.lanternCourt]: P.staticMenuHighlightTodaySplit,
  [THEME_ID.boldStreet]: P.staticMenuHighlightDishCards,
  /** Wurststand price board — not Döner dish cards. */
  [THEME_ID.mustardRail]: P.staticMenuHighlightPriceTiles,
  /** Heute am Fenster — lunch/evening mezze split. */
  [THEME_ID.tahiniWindow]: P.staticMenuHighlightTodaySplit,
};

/**
 * Curated presentation mixes per theme.
 *
 * UX contract: each theme is a **recipe**, not a token reskin. First-viewport
 * harmony (nav + hero) is intentional; downstream blocks match vertical jobs-to-be-done.
 * Menu arrangements are curated per theme (see `specs/components/menu-variants.md`).
 *
 * Footer arrangements (pick one job — do not clone):
 * - **Colophon** — editorial brand seal; last impression after a ritual/quiet page.
 * - **Standard** — hospitality desk columns (brand · contact · hours); warm visit sites.
 * - **Directory** — discovery board (hours + contact + social); Signal/nightlife cafés.
 * - **Utility** — conversion dock (call / mail / find us); street & nomad vendors.
 */
export const THEME_BLOCK_PRESENTATIONS: Record<ThemeId, ThemeBlockPresentations> = {
  /** Fine-dining dark — magazine editorial split + Curtain open ritual. */
  [THEME_ID.michelinDark]: {
    [T.navigation]: P.navigationCurtain,
    [T.hero]: P.heroEditorialSplit,
    [T.staticMenu]: P.staticMenuEditorialBoard,
    [T.dynamicMenu]: P.dynamicMenuFlow,
    [T.chefStory]: P.chefStoryNotes,
    [T.kitchenTeam]: P.kitchenTeamLead,
    [T.imageGallery]: P.imageGalleryLedger,
    [T.reservation]: P.reservationEditorialForm,
    [T.tastingTimeline]: P.tastingMenuPairingCards,
    [T.allergyForm]: P.allergyCaptureSecureForm,
    [T.drinksList]: P.drinksListSheet,
    [T.privatePartyInquiry]: P.privatePartyInquirySalon,
    /** Brand seal after tasting / reservation — not an info grid. */
    [T.footer]: P.footerColophon,
    [T.contact]: P.contactLedger,
    /** Quiet editorial — transparent icon, not a brand disc. */
    [T.backToTop]: P.backToTopPill,
    [T.contactChannels]: P.contactChannelsFab,
    [T.announcementBar]: P.announcementBarBand,
    /** Nav×Utility behaviour harmony — see navUtilityHarmony.ts */
    [T.utilityBar]: P.utilityBarStatic,
    [T.legalImprint]: P.legalImprintBlock,
    [T.legalPrivacy]: P.legalPrivacyBlock,
  },
  /**
   * Limestone orangery — specimen pedestal (Coffee Spotlight) + frosted glass rail.
   * Not Michelin curtain/editorial, not cafe Signal.
   */
  [THEME_ID.limestoneOrangery]: {
    [T.navigation]: P.navigationRail,
    [T.hero]: P.heroCoffeeSpotlight,
    [T.staticMenu]: P.staticMenuGrid,
    [T.dynamicMenu]: P.dynamicMenuFlow,
    [T.chefStory]: P.chefStoryKitchenStrip,
    [T.imageGallery]: P.imageGalleryFilmstrip,
    [T.reservation]: P.reservationFormEmbed,
    [T.tastingTimeline]: P.tastingMenuCourseTimeline,
    [T.allergyForm]: P.allergyCaptureSecureForm,
    [T.drinksList]: P.drinksListStatic,
    [T.privatePartyInquiry]: P.privatePartyInquiryForm,
    [T.footer]: P.footerStandard,
    [T.contact]: P.contactVisit,
    /** Glasshouse daylight — transparent icon over limestone. */
    [T.backToTop]: P.backToTopPill,
    [T.contactChannels]: P.contactChannelsRail,
    [T.announcementBar]: P.announcementBarBand,
    /** Nav×Utility behaviour harmony — see navUtilityHarmony.ts */
    [T.utilityBar]: P.utilityBarSticky,
    [T.legalImprint]: P.legalImprintBlock,
    [T.legalPrivacy]: P.legalPrivacyBlock,
  },
  /**
   * Moorland estate — cinematic grounds (Full Bleed) + Overlay chrome over photography.
   * Not Tile capsule, not Michelin curtain.
   */
  [THEME_ID.moorlandHearth]: {
    [T.navigation]: P.navigationOverlay,
    [T.hero]: P.heroFullBleedImage,
    [T.staticMenu]: P.staticMenuDishCards,
    [T.dynamicMenu]: P.dynamicMenuFlow,
    [T.chefStory]: P.chefStoryQuoteLed,
    [T.imageGallery]: P.imageGalleryMosaic,
    [T.reservation]: P.reservationEditorialForm,
    [T.tastingTimeline]: P.tastingMenuPairingCards,
    [T.allergyForm]: P.allergyCaptureSecureForm,
    [T.drinksList]: P.drinksListCellar,
    [T.privatePartyInquiry]: P.privatePartyInquirySalon,
    [T.footer]: P.footerDirectory,
    [T.contact]: P.contactDesk,
    /** Over photography — light glyph, not a filled disc. */
    [T.backToTop]: P.backToTopPill,
    [T.contactChannels]: P.contactChannelsRail,
    [T.announcementBar]: P.announcementBarBand,
    /** Nav×Utility behaviour harmony — see navUtilityHarmony.ts */
    [T.utilityBar]: P.utilityBarStatic,
    [T.legalImprint]: P.legalImprintBlock,
    [T.legalPrivacy]: P.legalPrivacyBlock,
  },
  /** Neighborhood brasserie — warm story zine, editorial brand rail, category photography. */
  [THEME_ID.bistroBrasserie]: {
    [T.navigation]: P.navigationRail,
    [T.hero]: P.heroCollageStory,
    [T.staticMenu]: P.staticMenuCategoryGallery,
    [T.dynamicMenu]: P.dynamicMenuFlow,
    [T.chefStory]: P.chefStoryBlock,
    [T.imageGallery]: P.imageGalleryMosaic,
    [T.reservation]: P.reservationFormEmbed,
    [T.tastingTimeline]: P.tastingMenuCourseTimeline,
    [T.allergyForm]: P.allergyCaptureSecureForm,
    /** Classic visit desk: address, phone, hours at a glance. */
    [T.footer]: P.footerStandard,
    [T.contact]: P.contactVisit,
    [T.backToTop]: P.backToTopPill,
    [T.contactChannels]: P.contactChannelsRail,
    [T.announcementBar]: P.announcementBarBand,
    /** Nav×Utility behaviour harmony — see navUtilityHarmony.ts */
    [T.utilityBar]: P.utilityBarSticky,
    [T.legalImprint]: P.legalImprintBlock,
    [T.legalPrivacy]: P.legalPrivacyBlock,
  },
  /**
   * Counter film (Recipe B) — Counter Bleed + Curtain whisper, Chef’s Cut Beliebt,
   * omakase timeline, compact Messenger sitting strip, Grid Speisekarte (wide columns —
   * not Michelin’s narrow Editorial Board pipe). Filmstrip is About-owned (under
   * Kitchen Team), not a Home mid-page strip.
   * Distinct from Monolith+Compass (Recipe A retired) and Quiet Luxury split.
   */
  [THEME_ID.hinokiCounter]: {
    [T.navigation]: P.navigationCurtain,
    [T.hero]: P.heroCounterBleed,
    [T.staticMenu]: P.staticMenuGrid,
    [T.dynamicMenu]: P.dynamicMenuFlow,
    [T.chefStory]: P.chefStoryQuoteLed,
    [T.imageGallery]: P.imageGalleryFilmstrip,
    [T.reservation]: P.reservationMessengerBook,
    [T.tastingTimeline]: P.tastingMenuCourseTimeline,
    [T.allergyForm]: P.allergyCaptureSecureForm,
    [T.footer]: P.footerColophon,
    [T.contact]: P.contactLedger,
    [T.backToTop]: P.backToTopPill,
    [T.contactChannels]: P.contactChannelsRail,
    [T.announcementBar]: P.announcementBarBand,
    /** Nav×Utility behaviour harmony — see navUtilityHarmony.ts */
    [T.utilityBar]: P.utilityBarCollapse,
    [T.legalImprint]: P.legalImprintBlock,
    [T.legalPrivacy]: P.legalPrivacyBlock,
  },
  /**
   * Vietnamese neighborhood — Broth Steam on Home (vapor over the bowl).
   * About uses Collage Story. Frosted Rail is mist, not Tile’s capsule pill.
   */
  [THEME_ID.lotusSteam]: {
    [T.navigation]: P.navigationRail,
    [T.hero]: P.heroBrothSteam,
    [T.staticMenu]: P.staticMenuCategoryGallery,
    [T.dynamicMenu]: P.dynamicMenuFlow,
    [T.chefStory]: P.chefStoryKitchenStrip,
    [T.imageGallery]: P.imageGalleryFilmstrip,
    [T.reservation]: P.reservationFormEmbed,
    [T.tastingTimeline]: P.tastingMenuCourseTimeline,
    [T.allergyForm]: P.allergyCaptureSecureForm,
    [T.footer]: P.footerColophon,
    [T.contact]: P.contactThreshold,
    /** Mist / vapor lightness — transparent icon. */
    [T.backToTop]: P.backToTopPill,
    [T.contactChannels]: P.contactChannelsRail,
    [T.announcementBar]: P.announcementBarBand,
    /** Nav×Utility behaviour harmony — see navUtilityHarmony.ts */
    [T.utilityBar]: P.utilityBarSticky,
    [T.legalImprint]: P.legalImprintBlock,
    [T.legalPrivacy]: P.legalPrivacyBlock,
  },
  /**
   * Vietnamese tiled shopfront — cinematic facade, capsule over photography,
   * chip Speisekarte, visit-desk Standard footer. Not a second Lantern court.
   */
  [THEME_ID.tileAwning]: {
    [T.navigation]: P.navigationRail,
    [T.hero]: P.heroFullBleedImage,
    [T.staticMenu]: P.staticMenuCategoryChips,
    [T.dynamicMenu]: P.dynamicMenuFlow,
    [T.chefStory]: P.chefStoryQuoteLed,
    [T.imageGallery]: P.imageGalleryMosaic,
    [T.reservation]: P.reservationFormEmbed,
    [T.tastingTimeline]: P.tastingMenuCourseTimeline,
    [T.allergyForm]: P.allergyCaptureSecureForm,
    [T.footer]: P.footerStandard,
    [T.contact]: P.contactVisit,
    /** Busy facade photos — solid corner disc for contrast. */
    [T.backToTop]: P.backToTopCorner,
    [T.contactChannels]: P.contactChannelsFab,
    [T.announcementBar]: P.announcementBarBand,
    /** Nav×Utility behaviour harmony — see navUtilityHarmony.ts */
    [T.utilityBar]: P.utilityBarStatic,
    [T.legalImprint]: P.legalImprintBlock,
    [T.legalPrivacy]: P.legalPrivacyBlock,
  },
  /**
   * Pan-Asian court — Quiet Luxury reveal + Curtain whisper (not Tile full-bleed
   * capsule). Dish-card Speisekarte, masonry album, Directory close.
   */
  [THEME_ID.lanternCourt]: {
    [T.navigation]: P.navigationCurtain,
    [T.hero]: P.heroQuietLuxury,
    [T.staticMenu]: P.staticMenuDishCards,
    [T.dynamicMenu]: P.dynamicMenuFlow,
    [T.chefStory]: P.chefStoryBlock,
    [T.imageGallery]: P.imageGalleryMasonry,
    [T.reservation]: P.reservationEditorialForm,
    [T.tastingTimeline]: P.tastingMenuCourseTimeline,
    [T.allergyForm]: P.allergyCaptureSecureForm,
    [T.footer]: P.footerDirectory,
    [T.contact]: P.contactDesk,
    [T.backToTop]: P.backToTopPill,
    [T.contactChannels]: P.contactChannelsRail,
    [T.announcementBar]: P.announcementBarBand,
    /** Nav×Utility behaviour harmony — see navUtilityHarmony.ts */
    [T.utilityBar]: P.utilityBarStatic,
    [T.legalImprint]: P.legalImprintBlock,
    [T.legalPrivacy]: P.legalPrivacyBlock,
  },
  /** Industrial third-wave — product pedestal hero, Signal social sheet. */
  [THEME_ID.brutalistGrind]: {
    [T.navigation]: P.navigationSignal,
    [T.hero]: P.heroCoffeeSpotlight,
    [T.openingHours]: P.simpleOpeningHours,
    [T.addressMap]: P.addressMapBlock,
    [T.liveBakeryCounter]: P.liveBakeryCounterList,
    [T.socialFeed]: P.socialFeedGrid,
    [T.imageGallery]: P.imageGalleryMosaic,
    [T.coffeeSubscription]: P.coffeeSubscriptionMatrix,
    [T.preOrderPickup]: P.preOrderPickupForm,
    /** Signal ↔ social discovery board (hours + contact + profiles). */
    [T.footer]: P.footerDirectory,
    [T.contact]: P.contactDesk,
    /** Bold industrial — corner disc. */
    [T.backToTop]: P.backToTopCorner,
    [T.contactChannels]: P.contactChannelsFab,
    [T.announcementBar]: P.announcementBarBand,
    /** Nav×Utility behaviour harmony — see navUtilityHarmony.ts */
    [T.utilityBar]: P.utilityBarCollapse,
    [T.legalImprint]: P.legalImprintBlock,
    [T.legalPrivacy]: P.legalPrivacyBlock,
  },
  /** Artisan earth-tone cafe — cinematic interior full-bleed, transparent overlay nav. */
  [THEME_ID.earthClay]: {
    [T.navigation]: P.navigationRail,
    [T.hero]: P.heroFullBleedImage,
    [T.openingHours]: P.simpleOpeningHours,
    [T.addressMap]: P.addressMapBlock,
    [T.liveBakeryCounter]: P.liveBakeryCounterList,
    [T.socialFeed]: P.socialFeedGrid,
    [T.imageGallery]: P.imageGalleryLedger,
    [T.coffeeSubscription]: P.coffeeSubscriptionMatrix,
    [T.preOrderPickup]: P.preOrderPickupForm,
    /** Warm hospitality columns (distinct from Brutalist’s Directory). */
    [T.footer]: P.footerStandard,
    [T.contact]: P.contactVisit,
    [T.backToTop]: P.backToTopPill,
    [T.contactChannels]: P.contactChannelsRail,
    [T.announcementBar]: P.announcementBarBand,
    /** Nav×Utility behaviour harmony — see navUtilityHarmony.ts */
    [T.utilityBar]: P.utilityBarStatic,
    [T.legalImprint]: P.legalImprintBlock,
    [T.legalPrivacy]: P.legalPrivacyBlock,
  },
  /** Nightlife electric — motion-first video hero, Signal social sheet. */
  [THEME_ID.neonNoir]: {
    [T.navigation]: P.navigationSignal,
    [T.hero]: P.heroVideoAtmosphere,
    [T.drinksList]: P.drinksListStatic,
    [T.cocktailSpotlight]: P.dynamicCocktailSpotlight,
    [T.privatePartyInquiry]: P.privatePartyInquiryForm,
    [T.imageGallery]: P.imageGalleryFilmstrip,
    [T.liveTapList]: P.liveTapListSync,
    [T.vipTableBooking]: P.vipTableBookingStrip,
    /** Tonight’s hours + social discovery (Signal pairing). */
    [T.footer]: P.footerDirectory,
    [T.contact]: P.contactDesk,
    /** High-contrast disc over dark video. */
    [T.backToTop]: P.backToTopCorner,
    [T.contactChannels]: P.contactChannelsFab,
    [T.announcementBar]: P.announcementBarBand,
    /** Nav×Utility behaviour harmony — see navUtilityHarmony.ts */
    [T.utilityBar]: P.utilityBarCollapse,
    [T.legalImprint]: P.legalImprintBlock,
    [T.legalPrivacy]: P.legalPrivacyBlock,
  },
  /** Art-deco lounge — partial reveal luxury, Curtain open ritual. */
  [THEME_ID.velvetVintage]: {
    [T.navigation]: P.navigationCurtain,
    [T.hero]: P.heroQuietLuxury,
    [T.drinksList]: P.drinksListStatic,
    [T.cocktailSpotlight]: P.dynamicCocktailSpotlight,
    [T.privatePartyInquiry]: P.privatePartyInquiryForm,
    [T.imageGallery]: P.imageGallerySpotlight,
    [T.liveTapList]: P.liveTapListSync,
    [T.vipTableBooking]: P.vipTableBookingStrip,
    /** Curtain + quiet luxury → brand seal (not a nightlife directory board). */
    [T.footer]: P.footerColophon,
    [T.contact]: P.contactLedger,
    [T.backToTop]: P.backToTopPill,
    [T.contactChannels]: P.contactChannelsRail,
    [T.announcementBar]: P.announcementBarBand,
    /** Nav×Utility behaviour harmony — see navUtilityHarmony.ts */
    [T.utilityBar]: P.utilityBarStatic,
    [T.legalImprint]: P.legalImprintBlock,
    [T.legalPrivacy]: P.legalPrivacyBlock,
  },
  /** Döner / grill Imbiss — ledger dock + utility call hero; Street Board Speisekarte. */
  [THEME_ID.boldStreet]: {
    [T.navigation]: P.navigationLedger,
    [T.hero]: P.heroUtilityReservation,
    [T.staticMenu]: P.staticMenuStreetBoard,
    [T.staticLocation]: P.staticLocationText,
    [T.simpleMenuStrip]: P.simpleMenuStrip,
    [T.weeklySchedule]: P.weeklyScheduleRouteStrip,
    [T.cateringInquiry]: P.cateringInquiryForm,
    [T.faq]: P.faqAccordion,
    [T.imageGallery]: P.imageGalleryFilmstrip,
    [T.liveGps]: P.liveGpsLocationEmbed,
    [T.popUpTicket]: P.popUpTicketPurchaseCard,
    /** Call / mail / find-the-truck dock. */
    [T.footer]: P.footerUtility,
    [T.contact]: P.contactThreshold,
    [T.backToTop]: P.backToTopCorner,
    [T.contactChannels]: P.contactChannelsFab,
    [T.announcementBar]: P.announcementBarBand,
    /** Nav×Utility behaviour harmony — see navUtilityHarmony.ts */
    [T.utilityBar]: P.utilityBarCarousel,
    [T.legalImprint]: P.legalImprintBlock,
    [T.legalPrivacy]: P.legalPrivacyBlock,
  },
  /**
   * Mustard Rail — Signal nav + Reel Fade stand facade (not Ledger + Utility).
   * Speisekarte stays Street Board; highlight = Price Tiles.
   */
  [THEME_ID.mustardRail]: {
    [T.navigation]: P.navigationSignal,
    [T.hero]: P.heroReelFade,
    [T.staticMenu]: P.staticMenuStreetBoard,
    [T.imageGallery]: P.imageGalleryMosaic,
    [T.footer]: P.footerDirectory,
    [T.contact]: P.contactVisit,
    /** Street board energy — corner disc. */
    [T.backToTop]: P.backToTopCorner,
    [T.contactChannels]: P.contactChannelsFab,
    [T.announcementBar]: P.announcementBarBand,
    /** Nav×Utility behaviour harmony — see navUtilityHarmony.ts */
    [T.utilityBar]: P.utilityBarCollapse,
    [T.legalImprint]: P.legalImprintBlock,
    [T.legalPrivacy]: P.legalPrivacyBlock,
  },
  /**
   * Tahini Window — Rail + Collage Story (not Signal/Full Bleed, not Ledger/Utility).
   * Speisekarte Street Board; highlight = Today Split.
   */
  [THEME_ID.tahiniWindow]: {
    [T.navigation]: P.navigationRail,
    [T.hero]: P.heroCollageStory,
    [T.staticMenu]: P.staticMenuStreetBoard,
    [T.imageGallery]: P.imageGalleryFilmstrip,
    [T.footer]: P.footerStandard,
    [T.contact]: P.contactThreshold,
    /** Soft herb-stall calm — transparent icon. */
    [T.backToTop]: P.backToTopPill,
    [T.contactChannels]: P.contactChannelsRail,
    [T.announcementBar]: P.announcementBarBand,
    /** Nav×Utility behaviour harmony — see navUtilityHarmony.ts */
    [T.utilityBar]: P.utilityBarSticky,
    [T.legalImprint]: P.legalImprintBlock,
    [T.legalPrivacy]: P.legalPrivacyBlock,
  },
  /**
   * Coastal nomad — Reel Fade + Capsule (theme-widened) + wide truck boards
   * (not Bold Street’s narrow strip / stop column).
   */
  [THEME_ID.cleanNomad]: {
    [T.navigation]: P.navigationRail,
    [T.hero]: P.heroReelFade,
    [T.staticLocation]: P.staticLocationStop,
    [T.simpleMenuStrip]: P.simpleMenuBoard,
    [T.weeklySchedule]: P.weeklyScheduleRouteBoard,
    [T.cateringInquiry]: P.cateringInquiryForm,
    [T.faq]: P.faqAccordion,
    [T.imageGallery]: P.imageGalleryLedger,
    [T.liveGps]: P.liveGpsLocationEmbed,
    [T.popUpTicket]: P.popUpTicketPurchaseCard,
    /** Equal board — brand | contact | hours (Utility left-stacked on wide truck pages). */
    [T.footer]: P.footerDirectory,
    [T.contact]: P.contactDesk,
    /** Truck utility — readable corner disc over boards. */
    [T.backToTop]: P.backToTopCorner,
    [T.contactChannels]: P.contactChannelsFab,
    [T.announcementBar]: P.announcementBarBand,
    /** Nav×Utility behaviour harmony — see navUtilityHarmony.ts */
    [T.utilityBar]: P.utilityBarStatic,
    [T.legalImprint]: P.legalImprintBlock,
    [T.legalPrivacy]: P.legalPrivacyBlock,
  },
};

/**
 * About-page hero arrangement when it must not repeat the Home hero.
 * Unlisted themes keep `THEME_BLOCK_PRESENTATIONS` hero (same style on both pages).
 */
export const THEME_ABOUT_HERO_PRESENTATIONS: Partial<
  Record<ThemeId, RegistryBlockPresentation>
> = {
  /**
   * Home is one bowl and rising vapor. About is a small house album —
   * three plates, type on the paper — so the story page is not a second broth hero.
   */
  [THEME_ID.lotusSteam]: P.heroCollageStory,
  /**
   * Home is cinematic Full-Bleed facade. About is Editorial Split —
   * one plate and shop copy, not a second awning photo.
   */
  [THEME_ID.tileAwning]: P.heroEditorialSplit,
  /**
   * Home is Quiet Luxury (lantern at the edge of the frame). About is a
   * sharing-plate collage — house memory, not a second partial reveal.
   */
  [THEME_ID.lanternCourt]: P.heroCollageStory,
  /**
   * Home is Editorial Split. About is Quiet Luxury —
   * a restrained reveal for the house story.
   */
  [THEME_ID.michelinDark]: P.heroQuietLuxury,
  /**
   * Home is a glasshouse specimen pedestal. About is Editorial Split —
   * house copy beside one plate, not a second coffee-style spotlight.
   */
  [THEME_ID.limestoneOrangery]: P.heroEditorialSplit,
  /**
   * Home is Full Bleed grounds. About is a house-album collage —
   * memory of the Gutshof, not a second cinematic still.
   */
  [THEME_ID.moorlandHearth]: P.heroCollageStory,
  /**
   * Home is Collage Story. About is Editorial Split —
   * narrative copy beside a single plate instead of a second zine stack.
   */
  [THEME_ID.bistroBrasserie]: P.heroEditorialSplit,
  /**
   * Home is Counter Bleed (nigiri night). About is Editorial Split —
   * venue photo + house story copy, not a second “Präzision an der Theke”.
   */
  [THEME_ID.hinokiCounter]: P.heroEditorialSplit,
};

/** About `/about-us` hero. Falls back to the theme's Home hero arrangement. */
export function resolveAboutHeroPresentation(themeId?: string): RegistryBlockPresentation {
  if (themeId) {
    const curated = THEME_ABOUT_HERO_PRESENTATIONS[themeId as ThemeId];
    if (curated) {
      return curated;
    }
  }
  const fromTheme = themeId
    ? THEME_BLOCK_PRESENTATIONS[themeId as ThemeId]?.[T.hero]
    : undefined;
  return fromTheme ?? P.heroEditorialSplit;
}

/** Home highlight Static_Menu presentation — may differ from Menu-page full arrangement. */
export function resolveHighlightMenuPresentation(
  themeId?: string,
): RegistryBlockPresentation {
  if (themeId) {
    const curated = THEME_HIGHLIGHT_MENU_PRESENTATIONS[themeId as ThemeId];
    if (curated) {
      return curated;
    }
  }
  const fromTheme = themeId
    ? THEME_BLOCK_PRESENTATIONS[themeId as ThemeId]?.[T.staticMenu]
    : undefined;
  return fromTheme ?? P.staticMenuGrid;
}
